/** @jsx jsx */
import React, { useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import AkChevronDown from '@atlaskit/icon/core/chevron-down';

import SettingsIcon from '@atlaskit/icon/core/migration/settings';
import AkSettingsIcon from '@atlaskit/icon/core/settings';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { appConfigurationPromptEntryPoint } from '@atlassian/jira-development-app-configuration-prompt/entrypoint.tsx';
import type { Location } from '@atlassian/jira-development-app-configuration-prompt/src/common/types.tsx';
import { SummaryItem } from '@atlassian/jira-development-summary-common/src/ui/summary-item/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useUserPreferredWorkItemLayout } from '@atlassian/jira-issue-view-layout/src/services/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import messages from './messages.tsx';

type Props = {
	location: Location;
	panelHasOtherPrompts?: boolean;
};

export const AppConfigurationPromptSummaryCurrent = ({
	location,
	panelHasOtherPrompts = false,
}: Props) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const isJiraAdmin = useIsAdmin();
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		appConfigurationPromptEntryPoint,
		{
			location,
			cloudId,
		},
	);
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();
	const isWideViewLayout = userPreferredLayout === 'WIDE';

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isSiteAdmin || isJiraAdmin) {
			entryPointActions.load();
		}
	}, [entryPointActions, isSiteAdmin, isJiraAdmin]);

	if (!isSiteAdmin && !isJiraAdmin) {
		return null;
	}

	return (
		<JiraEntryPointContainer
			id="jswDevelopmentSummaryAppConfigPromptEntryPoint"
			packageName="development-field"
			entryPointReferenceSubject={entryPointReferenceSubject}
			errorFallback="flag"
			runtimeProps={{
				location,
				cloudId,
				getDropdownTrigger: ({ ref, onClick, appIcon, ...props }) => (
					<div css={wrapperStyles}>
						<SummaryItem
							title={
								<>
									{location === 'DEVELOPMENT_PANEL' && formatMessage(messages.codeToolsPromptLabel)}
									{location === 'RELEASES_PANEL' &&
										formatMessage(messages.deploymentToolsPromptLabel)}
									{location === 'SECURITY_PANEL' &&
										formatMessage(messages.securityToolsPromptLabel)}
								</>
							}
							icon={
								appIcon || isWideViewLayout ? (
									<AkSettingsIcon color={token('color.link')} label="finish-tool-setup-icon" />
								) : (
									<SettingsIcon spacing="spacious" label="finish-tool-setup-icon" />
								)
							}
							secondaryData={
								isWideViewLayout ? (
									<AkChevronDown
										label="providers-dropdown"
										size="small"
										color={token('color.link')}
									/>
								) : (
									<ChevronDownIcon spacing="spacious" label="providers-dropdown" size="small" />
								)
							}
							reference={ref}
							{...props}
							onClick={onClick}
							linkFormatting
							data-testid="development-summary-app-config-prompt-field.ui.summary-item"
							disableAnimationOnUpdates
						/>
						<Box xcss={lozengeWrapperStyles}>
							<Lozenge appearance="moved">{formatMessage(messages.pendingLozenge)}</Lozenge>
						</Box>
						{panelHasOtherPrompts && <div css={separatorStyles} />}
					</div>
				),
			}}
		/>
	);
};

export const AppConfigurationPromptSummaryNext = ({
	location,
	panelHasOtherPrompts = false,
}: Props) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const isSiteAdmin = useIsSiteAdmin();
	const isJiraAdmin = useIsAdmin();
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		appConfigurationPromptEntryPoint,
		{
			location,
			cloudId,
		},
	);
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();
	const isWideViewLayout = userPreferredLayout === 'WIDE';

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (isSiteAdmin || isJiraAdmin) {
			entryPointActions.load();
		}
	}, [entryPointActions, isSiteAdmin, isJiraAdmin]);

	if (!isSiteAdmin && !isJiraAdmin) {
		return null;
	}

	return (
		<JiraEntryPointContainer
			id="jswDevelopmentSummaryAppConfigPromptEntryPoint"
			packageName="development-field"
			entryPointReferenceSubject={entryPointReferenceSubject}
			errorFallback="flag"
			runtimeProps={{
				location,
				cloudId,
				getDropdownTrigger: ({ ref, onClick, appIcon, ...props }) => (
					<div css={wrapperStyles}>
						<SummaryItem
							title={
								<>
									{location === 'DEVELOPMENT_PANEL' && formatMessage(messages.codeToolsPromptLabel)}
									{location === 'RELEASES_PANEL' &&
										formatMessage(messages.deploymentToolsPromptLabel)}
									{location === 'SECURITY_PANEL' &&
										formatMessage(messages.securityToolsPromptLabel)}
								</>
							}
							icon={
								appIcon || isWideViewLayout ? (
									<AkSettingsIcon color={token('color.link')} label="finish-tool-setup-icon" />
								) : (
									<SettingsIcon spacing="none" label="finish-tool-setup-icon" />
								)
							}
							secondaryData={
								<Lozenge appearance="moved">{formatMessage(messages.pendingLozenge)}</Lozenge>
							}
							reference={ref}
							{...props}
							onClick={onClick}
							linkFormatting
							data-testid="development-summary-app-config-prompt-field.ui.summary-item"
							disableAnimationOnUpdates
							forceVisibleSecondary
							secondaryDataIconAfter={
								isWideViewLayout ? (
									<AkChevronDown
										label="providers-dropdown"
										size="small"
										color={token('color.link')}
									/>
								) : (
									<ChevronDownIcon
										spacing="spacious"
										label="providers-dropdown"
										size="small"
										color={token('color.link')}
									/>
								)
							}
						/>
						{/* todo: add separator */}
						{panelHasOtherPrompts && <></>}
					</div>
				),
			}}
		/>
	);
};

export const AppConfigurationPromptSummary = componentWithCondition(
	() => expVal('jiv-21227_dev_panel_collapsibility_exp', 'isEnabled', false),
	AppConfigurationPromptSummaryNext,
	AppConfigurationPromptSummaryCurrent,
);

const wrapperStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	marginLeft: '0',
});

const lozengeWrapperStyles = xcss({
	marginTop: 'space.100',
});

const separatorStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),

	borderBottom: `2px solid ${token('color.border')}`,
	marginTop: token('space.150'),
	marginBottom: token('space.150'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '-5px',
	transform: 'translateX(-5px)',
});
